import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { clearReadCache } from "@/lib/pwa";

const AuthContext = createContext(null);

// Pas kleuren toe op :root CSS-variabelen — updatet direct sidebar/buttons/badges
function applyBranding(primair, secundair) {
  if (!primair && !secundair) return;
  const root = document.documentElement;
  if (primair) {
    root.style.setProperty("--sage", primair);
    // Bereken 10% tint voor sage-100
    root.style.setProperty("--sage-100", primair + "1a");
  }
  if (secundair) {
    root.style.setProperty("--gold", secundair);
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | authed | anon

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setStatus("authed");
      // Tenant-branding: gebruik /tenant/me (werkt voor alle rollen).
      // Voor niet-superadmin proberen we niet /organizations (403 voor
      // locatiemanager en anderen); /tenant/me is tenant-scoped.
      try {
        const r = await api.get("/tenant/me");
        if (r.data) applyBranding(r.data.kleur_primair, r.data.kleur_secundair);
      } catch { /* fallback op default kleuren */ }
    } catch (e) {
      setUser(null);
      setStatus("anon");
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setStatus("anon");
      return;
    }
    // Publieke routes: geen auth-probe → geen 401 in console
    if (typeof window !== "undefined") {
      const p = window.location.pathname || "";
      if (p.startsWith("/aanmelden/") || p.startsWith("/aanbod/") || p.startsWith("/status/") ||
          p.startsWith("/inschrijven") || p === "/" || p.startsWith("/marketing") ||
          p === "/login" || p.startsWith("/activeer") || p.startsWith("/registreer")) {
        setStatus("anon");
        return;
      }
    }
    // Try to restore session from httpOnly cookie
    fetchMe();
  }, [fetchMe]);

  const login = async (identifier, password) => {
    try {
      // Detecteer email vs. username: username bevat geen '@'
      const isUsername = identifier && !identifier.includes("@");
      const payload = isUsername
        ? { username: identifier, password }
        : { email: identifier, password };
      const { data } = await api.post("/auth/login", payload);
      // MFA-flow: geen user gezet, alleen challenge terug
      if (data.mfa_required) {
        return { ok: false, mfa_required: true, user_id: data.user_id, email: data.email };
      }
      setUser(data.user);
      setStatus("authed");
      clearReadCache();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const mfaChallenge = async (user_id, code) => {
    try {
      const { data } = await api.post("/auth/mfa/challenge", { user_id, code });
      setUser(data.user);
      setStatus("authed");
      clearReadCache();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    setUser(null);
    setStatus("anon");
    // Wis alle gecachete API-reads van de huidige gebruiker uit de PWA-cache.
    clearReadCache();
  };

  return (
    <AuthContext.Provider value={{ user, status, login, logout, mfaChallenge, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Permissie helper — spiegelt backend RBAC voor UI-verbergingen
const ROLE_PERMISSIONS = {
  superadmin: { "*": new Set(["read", "write", "delete", "correct"]) },
  locatiemanager: {
    dashboard: new Set(["read"]),
    locations: new Set(["read"]),
    groups: new Set(["read", "write"]),
    children: new Set(["read", "write"]),
    parents: new Set(["read", "write"]),
    contracts: new Set(["read", "write"]),
    staff: new Set(["read", "write"]),
    planning: new Set(["read", "write"]),
    // invoices bewust WEGGELATEN — locatiemanager mag facturering NIET zien.
    audit: new Set(["read"]),
    users: new Set(["read"]),
  },
  financieel: {
    dashboard: new Set(["read"]),
    locations: new Set(["read"]),
    parents: new Set(["read", "write"]),
    contracts: new Set(["read"]),
    children: new Set(["read"]),
    invoices: new Set(["read", "write", "correct"]),
    audit: new Set(["read"]),
  },
  pedagogisch: {
    locations: new Set(["read"]),
    children: new Set(["read"]),
    parents: new Set(["read"]),
    planning: new Set(["read", "write"]),
    groups: new Set(["read"]),
    staff: new Set(["read"]),
    schriftje: new Set(["read", "write"]),
    activiteiten: new Set(["read", "write"]),
    nieuwsbrief: new Set(["read"]),
    mentorbericht: new Set(["read", "write"]),
    communicatie: new Set(["read", "write"]),
    incidenten: new Set(["read", "write"]),
    medicatie: new Set(["read", "write"]),
  },
  ouder: {
    portaal: new Set(["read"]),
    children: new Set(["read"]),
    invoices: new Set(["read"]),
    schriftje: new Set(["read"]),
    nieuwsbrief: new Set(["read"]),
    mentorbericht: new Set(["read", "write"]),
    medicatie: new Set(["read", "write"]),
  },
};

// Content modules for higher roles
// NB: `wachtlijst` is bewust NIET aan locatiemanager toegekend — endpoints zijn
// nog niet location-scoped (zie 06_route_and_metric_contracts.yaml).
for (const r of ["superadmin", "directeur"]) {
  ROLE_PERMISSIONS[r] = ROLE_PERMISSIONS[r] || {};
  for (const mod of ["wachtlijst", "schriftje", "nieuwsbrief", "mentorbericht", "communicatie",
                      "webhooks", "exports", "reports", "aanmeldingen", "activiteiten",
                      "incidenten", "medicatie", "ggd"]) {
    if (!ROLE_PERMISSIONS[r][mod]) ROLE_PERMISSIONS[r][mod] = new Set(["read", "write", "delete"]);
  }
}
for (const mod of ["schriftje", "nieuwsbrief", "mentorbericht", "communicatie",
                    "webhooks", "exports", "reports", "aanmeldingen", "activiteiten",
                    "incidenten", "medicatie", "ggd"]) {
  ROLE_PERMISSIONS.locatiemanager[mod] = new Set(["read", "write", "delete"]);
}
// Directeur = tenant-brede management: geef ook wildcard
if (!ROLE_PERMISSIONS.directeur) ROLE_PERMISSIONS.directeur = {};
ROLE_PERMISSIONS.directeur["*"] = new Set(["read", "write", "delete", "correct"]);
// Administratie = admin-taken zonder gevoelige data (salaris/medisch)
ROLE_PERMISSIONS.administratie = {
  dashboard: new Set(["read"]),
  locations: new Set(["read"]),
  groups: new Set(["read"]),
  children: new Set(["read", "write"]),
  parents: new Set(["read", "write"]),
  contracts: new Set(["read", "write"]),
  invoices: new Set(["read", "write"]),
  aanmeldingen: new Set(["read", "write"]),
  wachtlijst: new Set(["read", "write"]),
  audit: new Set(["read"]),
  reports: new Set(["read"]),
};
if (ROLE_PERMISSIONS.financieel) {
  ROLE_PERMISSIONS.financieel.exports = new Set(["read", "write"]);
  ROLE_PERMISSIONS.financieel.webhooks = new Set(["read"]);
  ROLE_PERMISSIONS.financieel.reports = new Set(["read"]);
}
// Salary = gevoelige salaris/verlof/ziek-data. Alleen directeur + platform-super
// standaard. Financieel & administratie krijgen dit NIET (bereidt export voor
// via `exports`, niet via directe salaris-toegang).
ROLE_PERMISSIONS.superadmin.salary = new Set(["read", "write", "delete"]);
ROLE_PERMISSIONS.directeur.salary = new Set(["read", "write", "delete"]);
// platform_admin heeft al "*" wildcard

export function can(user, module, action = "read") {
  if (!user) return false;
  const p = ROLE_PERMISSIONS[user.role] || {};
  if (p["*"]) return p["*"].has(action);
  return (p[module] || new Set()).has(action);
}
